import { useAuth } from '@/context/AuthContext';

const PLAN_DETAILS: Record<string, {
  label: string;
  price: string;
  accent: string;
  icon: string;
  features: string[];
  desc: string;
}> = {
  free: {
    label: 'Gratis',
    price: '0€/mes',
    accent: '#a8b2c1',
    icon: 'ri-user-line',
    desc: 'Acceso básico a la plataforma.',
    features: [
      'Estadísticas básicas de equipos',
      'Foro de comunidad (lectura)',
      'Ranking de tipsters (top 10)',
      'Comparador de cuotas (3 casas)',
      'Ligas públicas (como espectador)',
    ],
  },
  premium: {
    label: 'Premium',
    price: '2,50€/mes',
    accent: '#00e87a',
    icon: 'ri-vip-crown-line',
    desc: 'Herramientas completas para apostadores serios.',
    features: [
      'Todo lo del plan Gratis',
      'Buscador de tendencias avanzado',
      'Alertas Dropping Odds (20/día)',
      'Gestor de bankroll personal',
      'Filtros cruzados sin límite',
      'Comparador completo (6 casas)',
      'Participar en ligas competitivas',
      'Publicar picks en el foro',
    ],
  },
  elite: {
    label: 'Elite',
    price: '5,00€/mes',
    accent: '#ffd700',
    icon: 'ri-vip-diamond-line',
    desc: 'El máximo nivel para profesionales del dato.',
    features: [
      'Todo lo del plan Premium',
      'Alertas Dropping Odds ilimitadas',
      'Alertas SMS en tiempo real',
      'Ligas privadas sin límite',
      'Badge Elite verificado en perfil',
      'Datos históricos (5 años)',
      'Soporte prioritario 24/7',
    ],
  },
};

const NEXT_PLANS: Record<string, string[]> = {
  free: ['premium', 'elite'],
  premium: ['elite'],
  elite: [],
};

export default function SubscriptionSection() {
  const { plan, openPaywallModal, isAdmin } = useAuth();

  const currentPlanKey = plan ?? 'free';
  const current = PLAN_DETAILS[currentPlanKey] ?? PLAN_DETAILS.free;
  const upgradePlans = NEXT_PLANS[currentPlanKey] ?? [];

  if (isAdmin) {
    return (
      <div className="bg-[#151719] border border-[#ff6b35]/30 rounded-2xl p-8 text-center">
        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#ff6b35]/10 mx-auto mb-4">
          <i className="ri-shield-star-line text-[#ff6b35] text-2xl"></i>
        </div>
        <h3 className="text-white font-black text-lg mb-2">Cuenta de Administrador</h3>
        <p className="text-[#6b7280] text-sm">Tienes acceso completo a todas las funciones sin suscripción.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Current plan card */}
      <div
        className="bg-[#151719] rounded-2xl overflow-hidden border"
        style={{ borderColor: `${current.accent}40` }}
      >
        {/* Top accent bar */}
        <div className="h-1" style={{ background: `linear-gradient(90deg, transparent, ${current.accent}, transparent)` }} />

        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            {/* Plan icon + info */}
            <div className="flex items-center gap-4 flex-1">
              <div
                className="w-14 h-14 flex items-center justify-center rounded-2xl flex-shrink-0"
                style={{ background: `${current.accent}15` }}
              >
                <i className={`${current.icon} text-2xl`} style={{ color: current.accent }}></i>
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="px-3 py-1 rounded-full text-xs font-black uppercase"
                    style={{ background: `${current.accent}20`, color: current.accent }}
                  >
                    Plan {current.label}
                  </span>
                  <span className="bg-[#00e87a]/10 text-[#00e87a] text-xs font-bold px-2.5 py-0.5 rounded-full flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill text-xs"></i>Activo
                  </span>
                </div>
                <p className="text-white font-black text-xl mt-1">{current.price}</p>
                <p className="text-[#6b7280] text-xs mt-0.5">{current.desc}</p>
              </div>
            </div>

            {/* Actions */}
            {currentPlanKey !== 'free' && (
              <div className="flex flex-col gap-2 md:w-48">
                <button
                  onClick={() => openPaywallModal()}
                  className="py-2.5 border border-[#2a2d3a] text-[#a8b2c1] text-sm rounded-xl hover:bg-white/5 cursor-pointer whitespace-nowrap transition-all text-center flex items-center justify-center gap-2"
                >
                  <i className="ri-refresh-line text-sm"></i>
                  Cambiar plan
                </button>
                <a
                  href="mailto:soporte@apuestamente.com?subject=Cancelar%20suscripci%C3%B3n"
                  className="py-2.5 border border-[#ef4444]/20 text-[#ef4444]/70 text-sm rounded-xl hover:bg-[#ef4444]/10 hover:text-[#ef4444] cursor-pointer whitespace-nowrap transition-all text-center flex items-center justify-center gap-2"
                >
                  <i className="ri-close-circle-line text-sm"></i>
                  Cancelar suscripción
                </a>
              </div>
            )}
          </div>

          {/* Features */}
          <div className="mt-5 pt-5 border-t border-[#2a2d3a]">
            <p className="text-[#6b7280] text-xs uppercase tracking-wider font-bold mb-3">
              Incluido en tu plan
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6">
              {current.features.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm text-[#a8b2c1]">
                  <div
                    className="w-4 h-4 flex items-center justify-center flex-shrink-0"
                    style={{ color: current.accent }}
                  >
                    <i className="ri-check-line text-sm"></i>
                  </div>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Upgrade options */}
      {upgradePlans.length > 0 && (
        <div>
          <p className="text-[#6b7280] text-xs uppercase tracking-wider font-bold mb-3">
            Planes disponibles
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {upgradePlans.map((planKey) => {
              const p = PLAN_DETAILS[planKey];
              return (
                <div
                  key={planKey}
                  className="bg-[#151719] border border-[#2a2d3a] hover:border-white/20 rounded-xl p-5 transition-all"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0"
                      style={{ background: `${p.accent}15` }}
                    >
                      <i className={`${p.icon} text-base`} style={{ color: p.accent }}></i>
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm">{p.label}</p>
                      <p className="text-[#6b7280] text-xs">{p.price}</p>
                    </div>
                  </div>
                  <p className="text-[#6b7280] text-xs mb-4">{p.desc}</p>
                  {/* Próximamente badge */}
                  <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-[#2a2d3a] justify-center">
                    <i className="ri-time-line text-[#6b7280] text-sm"></i>
                    <span className="text-[#6b7280] text-xs font-bold">Próximamente disponible</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cancel info */}
      {currentPlanKey !== 'free' && (
        <div className="bg-[#0d0f14] border border-[#2a2d3a] rounded-xl p-4 flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center text-[#6b7280] flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-base"></i>
          </div>
          <div>
            <p className="text-[#a8b2c1] text-xs font-medium mb-0.5">Cancelación sin permanencia</p>
            <p className="text-[#6b7280] text-xs leading-relaxed">
              Puedes cancelar tu suscripción en cualquier momento. Al cancelar,
              seguirás teniendo acceso hasta el final del período de facturación actual.
              Para cancelar, contáctanos en{' '}
              <a href="mailto:soporte@apuestamente.com" className="text-[#00e87a] hover:underline">
                soporte@apuestamente.com
              </a>
            </p>
          </div>
        </div>
      )}

      {/* Free plan CTA */}
      {currentPlanKey === 'free' && (
        <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-5 text-center">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#00e87a]/10 mx-auto mb-3">
            <i className="ri-vip-crown-line text-[#00e87a] text-lg"></i>
          </div>
          <p className="text-white font-bold text-sm mb-1">Los planes de pago estarán disponibles próximamente</p>
          <p className="text-[#6b7280] text-xs mb-4">
            Estamos preparando planes Premium y Elite. Te avisaremos cuando estén disponibles.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00e87a]/10 border border-[#00e87a]/20 rounded-xl">
            <i className="ri-time-line text-[#00e87a] text-sm"></i>
            <span className="text-[#00e87a] text-xs font-bold">Próximamente</span>
          </div>
        </div>
      )}
    </div>
  );
}
